import React, { useEffect, useRef } from 'react';
import { Layout } from 'antd';
import { Outlet, Navigate } from 'react-router-dom';
import { ROUTES } from '../../constants/routes';
import Sidebar from './Sidebar';
import Header from './Header';
import LoadingSpinner from '../feedback/LoadingSpinner';
import { useAuth } from '../../contexts/AuthContext';
import { useAppStore } from '../../stores/useAppStore';
import CreditAlertBanner from '../CreditAlert/CreditAlertBanner';
import { ErrorBoundary } from '../feedback/ErrorBoundary';
import OfflineBanner from '../feedback/OfflineBanner';
import OnboardingBanner from '../../../modules/settings/components/OnboardingBanner';
import NpsSurveyWidget from '../../../modules/nps/components/NpsSurveyWidget';
import { SupportWidget } from '../SupportWidget';
import SupportAssistantWidget from '../SupportAssistantWidget';
import JanusAgentWidget from '../JanusAgentWidget';
import SubscriptionGate from '../SubscriptionGate';
import { useNewSupportMessages } from '../../hooks/useNewSupportMessages';
import { useEnrichmentQueueDetector } from '../../hooks/useEnrichmentQueueDetector';
import { useJanusTaxonomy } from '../../hooks/useJanusTaxonomy';
import { useFeatureFlagsContext } from '../../features';
import { useCrossSellGlobalStream } from '../../../modules/cross-sell/hooks/useCrossSellGlobalStream';
import { useCrossSellToastDetector } from '../../../modules/cross-sell/hooks/useCrossSellToastDetector';

const { Content } = Layout;

/**
 * O tour guiado NÃO é montado aqui — e o que era montado não mostrava nada.
 *
 * MEDIDO em 2026-09-07: `ProductTour/ProductTour.tsx` era um stub que
 * renderizava `null`, avisava no console que estava deprecado e chamava
 * `onComplete()` na hora. O `onComplete` daqui gravava
 * `janus-tour-completed = 'true'` — ou seja, o produto carimbava
 * "tour concluído" para um tour que o usuário nunca viu, no primeiro
 * login dele. Fato fabricado sobre um ato que não aconteceu, e o carimbo
 * nem era lido por ninguém (`grep janus-tour-completed` = só esta linha).
 *
 * O motor de verdade existe INTEIRO e não é montado em lugar nenhum:
 * `hooks/useProductTour.ts` (Driver.js, com persistência cross-device pela
 * rota VIVA `/api/v1/product-tour` do BFF), `components/product-tour/
 * GuidedTour.tsx` e as SETE definições de `shared/data/tours/*.tour.ts`.
 * O que falta para ligá-lo não é código: são as **29 âncoras
 * `data-tour="..."`** que os sete tours apontam e que o produto renderiza
 * **zero vezes** (medido: `grep -ro 'data-tour="[^"]*"' src` não devolve
 * uma ocorrência fora dos próprios arquivos de tour).
 *
 * Por isso NÃO ligamos o `GuidedTour` nesta entrega: escolher, para cada
 * um dos 29 seletores, qual elemento de hoje "significa" aquele passo
 * seria adivinhar — e o texto de cada passo AFIRMA um recurso ("use
 * filtros para segmentar por indústria, porte ou score") que precisaria
 * ser conferido um a um. Copy de onboarding inventada é fabricação
 * apontada para quem vê o produto pela primeira vez.
 *
 * `janus-start-tour` (gravado pelo botão "ir para o dashboard COM tour"
 * do onboarding e pelo "Refazer o tour" do perfil) fica DE PÉ, sem ser
 * consumido: é o pedido do usuário, registrado e ainda não atendido. Um
 * recurso que falta é honesto; um carimbo de conclusão que ninguém
 * cumpriu, não. O guard que impede o tour de ser ligado sem âncora está
 * em `shared/components/__tests__/ancorasDoTour.test.ts`.
 */

const AppLayout: React.FC = () => {
  const { isAuthenticated, isLoading, user } = useAuth();
  const { sidebarCollapsed } = useAppStore();
  const { isEnabled: isFeatureEnabled } = useFeatureFlagsContext();
  const topBannerRef = useRef<HTMLDivElement>(null);

  // Global poller (30s) for new replies on the user's open support tickets.
  // No-ops when the `support` feature flag is off for the tenant — see the
  // hook for the gating logic. Mounted at the layout level so every
  // authenticated route gets the bell-blink notification without needing
  // to re-mount on navigation.
  useNewSupportMessages();

  // Load the bilingual job taxonomy (cargos + departamentos) from the central
  // ops-mgmt catalog once, app-wide. Refreshes the shared label maps so ICP
  // badges / selectors reflect catalog edits without a frontend deploy.
  useJanusTaxonomy();

  // Global poller for in-flight contact enrichments. Mirrors the support
  // detector pattern: walks the client-side enrichment queue store, polls
  // /contacts/enrichment-status/{id} for each entry still processing, and
  // fires toast + desktop notification on terminal transitions.
  useEnrichmentQueueDetector();

  // Cross-Sell global channel — one EventSource per-tenant + completion toast,
  // gated by the `cross_sell` feature flag (Plus+). Drives the header badge +
  // drawer so a job started on any screen surfaces everywhere (UX §5.3 / §4.5).
  const crossSellEnabled = isAuthenticated && isFeatureEnabled('cross_sell');
  useCrossSellGlobalStream(crossSellEnabled);
  useCrossSellToastDetector();

  // Keep --kanban-col-max-height in sync with the actual top bar height
  useEffect(() => {
    const el = topBannerRef.current;
    if (!el) return;
    const update = () => {
      const offset = el.getBoundingClientRect().height + 64 + 24 + 36 + 16; // banners + header + tabs + padding
      document.documentElement.style.setProperty('--kanban-col-max-height', `calc(100vh - ${offset}px)`);
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);


  if (isLoading) {
    return <LoadingSpinner fullScreen />;
  }

  if (!isAuthenticated) {
    return <Navigate to={ROUTES.LOGIN} replace />;
  }

  // Restricted JWT: only allow /change-password
  if (user?.restricted) {
    return <Navigate to={ROUTES.CHANGE_PASSWORD} replace />;
  }

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Sidebar />
      <Layout
        style={{
          marginLeft: sidebarCollapsed ? 80 : 240,
          transition: 'margin-left 0.2s',
        }}
      >
        <div ref={topBannerRef}>
          <CreditAlertBanner />
          <OfflineBanner />
          <OnboardingBanner />
        </div>
        <Header />
        <Content
          style={{
            margin: '24px 28px',
            paddingBottom: 160,
            minHeight: 280,
            background: 'var(--color-bg-layout)',
          }}
        >
          <ErrorBoundary>
            <SubscriptionGate>
              <Outlet />
            </SubscriptionGate>
          </ErrorBoundary>
        </Content>
        {/* NpsIndicator was mounted here alongside NpsSurveyWidget, which
            caused two NPS modals to pop up at the same time and each one
            re-opened after the other was dismissed. Keep only the widget. */}
        {isFeatureEnabled('nps') && <NpsSurveyWidget />}
        <SupportWidget />
        <SupportAssistantWidget />
        {isFeatureEnabled('agent') && <JanusAgentWidget />}
      </Layout>
    </Layout>
  );
};

export default AppLayout;
